import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageTitle, EmptyState } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Store, Plus, Pencil, Trash2, ImagePlus, X, Phone, Save } from "lucide-react";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";

const CATEGORIES = ["WO", "Fotografer", "MUA", "Catering", "Venue", "Dekorasi", "Musik", "Suvenir", "Lainnya"];
const EMPTY = {
  nama_vendor: "", kategori: "WO", logo: "", cover: "", deskripsi: "",
  kota: "", area_layanan: "", harga_mulai: 0, whatsapp: "",
  instagram: "", website: "", gallery: [], active: true,
};

// ============ Vendor Listing Admin ============
export function VendorListingAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/vendor-listing"); setItems(data); }
    catch { toast.error("Gagal memuat vendor"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      nama_vendor: v.nama_vendor || "", kategori: v.kategori || "WO",
      logo: v.logo || "", cover: v.cover || "", deskripsi: v.deskripsi || "",
      kota: v.kota || "", area_layanan: v.area_layanan || "",
      harga_mulai: v.harga_mulai || 0, whatsapp: v.whatsapp || "",
      instagram: v.instagram || "", website: v.website || "",
      gallery: Array.isArray(v.gallery) ? v.gallery : [],
      active: v.active !== false,
    });
    setOpen(true);
  };

  const uploadOne = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const b64 = await resizeImage(file, 1200);
      setForm((f) => ({ ...f, [key]: b64 }));
    } finally { setUploading(""); }
  };
  const uploadGallery = async (files) => {
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    setUploading("gallery");
    try {
      const room = Math.max(0, 10 - form.gallery.length);
      const encoded = await Promise.all(arr.slice(0, room).map((f) => resizeImage(f, 1200)));
      setForm((f) => ({ ...f, gallery: [...f.gallery, ...encoded] }));
    } finally { setUploading(""); }
  };
  const removeGallery = (i) => setForm((f) => ({ ...f, gallery: f.gallery.filter((_, x) => x !== i) }));

  const save = async () => {
    if (!form.nama_vendor.trim() || !form.cover || !form.deskripsi.trim() || !form.kota.trim() || !form.whatsapp.trim()) {
      toast.error("Nama, Kategori, Cover, Deskripsi, Kota, dan WhatsApp wajib diisi"); return;
    }
    const payload = { ...form, harga_mulai: parseInt(form.harga_mulai, 10) || 0 };
    try {
      if (editing) { await api.put(`/admin/vendor-listing/${editing.id}`, payload); toast.success("Vendor diperbarui"); }
      else { await api.post("/admin/vendor-listing", payload); toast.success("Vendor ditambahkan"); }
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };
  const remove = async (v) => {
    if (!window.confirm(`Hapus vendor "${v.nama_vendor}"?`)) return;
    try { await api.delete(`/admin/vendor-listing/${v.id}`); toast.success("Dihapus"); await load(); }
    catch { toast.error("Gagal menghapus"); }
  };
  const toggle = async (v) => {
    try {
      await api.put(`/admin/vendor-listing/${v.id}`, { ...v, active: !v.active });
      await load();
    } catch { toast.error("Gagal mengubah status"); }
  };

  return (
    <div>
      <PageTitle title="Katalog Vendor Pilihan" desc="Kelola vendor yang tampil pada menu Vendor Pilihan (paket Premium)." icon={Store} />
      <div className="mb-4 flex justify-end">
        <Button data-testid="admin-vendor-add" onClick={openNew} className="gap-1.5"><Plus className="h-4 w-4" /> Tambah Vendor</Button>
      </div>
      {loading ? <p className="text-sm text-muted-foreground">Memuat…</p> :
       items.length === 0 ? <EmptyState text="Belum ada vendor. Klik 'Tambah Vendor' untuk mulai." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((v) => (
            <Card key={v.id} data-testid={`admin-vendor-row-${v.id}`} className="flex flex-col p-4">
              <div className="flex items-start gap-3">
                {v.cover ? <img src={v.cover} alt="" className="h-16 w-16 rounded-md object-cover" /> :
                 <div className="flex h-16 w-16 items-center justify-center rounded-md bg-muted"><Store className="h-6 w-6 text-muted-foreground" /></div>}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{v.nama_vendor}</h3>
                    <Badge variant={v.active ? "default" : "outline"} className="text-[10px]">{v.active ? "Aktif" : "Nonaktif"}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{v.kategori} · {v.kota}</p>
                </div>
                <Switch data-testid={`admin-vendor-toggle-${v.id}`} checked={!!v.active} onCheckedChange={() => toggle(v)} />
              </div>
              <p className="mt-3 line-clamp-2 flex-1 text-sm text-muted-foreground">{v.deskripsi}</p>
              <div className="mt-3 flex gap-1.5">
                <Button data-testid={`admin-vendor-edit-${v.id}`} onClick={() => openEdit(v)} size="sm" variant="outline" className="flex-1 gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                <Button data-testid={`admin-vendor-delete-${v.id}`} onClick={() => remove(v)} size="sm" variant="outline" className="gap-1 text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Vendor" : "Tambah Vendor"}</DialogTitle>
            <DialogDescription className="sr-only">Form vendor</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div><Label className="text-xs">Nama Vendor *</Label><Input value={form.nama_vendor} onChange={(e) => setForm((f) => ({ ...f, nama_vendor: e.target.value }))} data-testid="admin-vendor-nama" /></div>
            <div>
              <Label className="text-xs">Kategori *</Label>
              <select data-testid="admin-vendor-kategori" className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm" value={form.kategori} onChange={(e) => setForm((f) => ({ ...f, kategori: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Kota *</Label><Input value={form.kota} onChange={(e) => setForm((f) => ({ ...f, kota: e.target.value }))} data-testid="admin-vendor-kota" placeholder="mis. Bandung" /></div>
            <div><Label className="text-xs">Area Layanan</Label><Input value={form.area_layanan} onChange={(e) => setForm((f) => ({ ...f, area_layanan: e.target.value }))} placeholder="mis. Jabodetabek" /></div>
            <div><Label className="text-xs">Harga Mulai (Rp)</Label><Input type="number" value={form.harga_mulai} onChange={(e) => setForm((f) => ({ ...f, harga_mulai: e.target.value }))} /></div>
            <div><Label className="text-xs">WhatsApp *</Label><Input value={form.whatsapp} onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))} data-testid="admin-vendor-wa" placeholder="0812xxx / 62812xxx" /></div>
            <div><Label className="text-xs">Instagram (username)</Label><Input value={form.instagram} onChange={(e) => setForm((f) => ({ ...f, instagram: e.target.value }))} placeholder="username_ig" /></div>
            <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} placeholder="https://..." /></div>
            <div className="md:col-span-2"><Label className="text-xs">Deskripsi Singkat *</Label><Textarea rows={2} value={form.deskripsi} onChange={(e) => setForm((f) => ({ ...f, deskripsi: e.target.value }))} data-testid="admin-vendor-deskripsi" /></div>
            {/* Cover & logo */}
            <div>
              <Label className="text-xs">Foto Cover *</Label>
              <div className="mt-1">
                {form.cover ? <img src={form.cover} alt="" className="h-24 w-full rounded-md object-cover" /> : null}
                <label className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs text-primary underline"><ImagePlus className="h-3 w-3" />{form.cover ? "Ganti Cover" : "Upload Cover"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadOne("cover", e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            </div>
            <div>
              <Label className="text-xs">Logo (opsional)</Label>
              <div className="mt-1">
                {form.logo ? <img src={form.logo} alt="" className="h-16 w-16 rounded-full object-cover" /> : null}
                <label className="mt-1 inline-flex cursor-pointer items-center gap-1 text-xs text-primary underline"><ImagePlus className="h-3 w-3" />{form.logo ? "Ganti Logo" : "Upload Logo"}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadOne("logo", e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            </div>
            {/* Gallery */}
            <div className="md:col-span-2">
              <Label className="text-xs">Galeri (5–10 foto, opsional)</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {form.gallery.map((src, i) => (
                  <div key={i} className="group relative h-16 w-16 overflow-hidden rounded-md border">
                    <img src={src} alt="" className="h-full w-full object-cover" />
                    <button onClick={() => removeGallery(i)} className="absolute right-0 top-0 rounded-bl bg-black/70 p-0.5 text-white opacity-0 group-hover:opacity-100"><X className="h-3 w-3" /></button>
                  </div>
                ))}
                {form.gallery.length < 10 && (
                  <label className="flex h-16 w-16 cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed border-border text-muted-foreground">
                    <ImagePlus className="h-4 w-4" /><span className="text-[9px]">Tambah</span>
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadGallery(e.target.files); e.target.value = ""; }} />
                  </label>
                )}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">{form.gallery.length}/10 foto</p>
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
              <Label>{form.active ? "Aktif — tampil di listing" : "Nonaktif — disembunyikan"}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="admin-vendor-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Budget Scenarios Admin ============
const EMPTY_SCENARIO = { nama: "", deskripsi: "", items: [{ kategori: "Venue", nominal: 0, catatan: "" }], active: true };

export function BudgetScenariosAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SCENARIO);

  const load = async () => {
    try { const { data } = await api.get("/admin/budget-scenarios"); setItems(data); }
    catch { toast.error("Gagal memuat"); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY_SCENARIO); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      nama: s.nama || "", deskripsi: s.deskripsi || "",
      items: (s.items && s.items.length ? s.items : EMPTY_SCENARIO.items).map((i) => ({ kategori: i.kategori || "", nominal: i.nominal || 0, catatan: i.catatan || "" })),
      active: s.active !== false,
    });
    setOpen(true);
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { kategori: "", nominal: 0, catatan: "" }] }));
  const removeItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, x) => x !== i) }));
  const setItem = (i, k, v) => setForm((f) => ({ ...f, items: f.items.map((it, x) => x === i ? { ...it, [k]: k === "nominal" ? parseInt(v, 10) || 0 : v } : it) }));
  const total = form.items.reduce((a, i) => a + (parseInt(i.nominal, 10) || 0), 0);

  const save = async () => {
    if (!form.nama.trim() || form.items.length === 0) { toast.error("Nama dan minimal 1 item wajib"); return; }
    const payload = { ...form, items: form.items.filter((i) => i.kategori.trim()) };
    try {
      if (editing) await api.put(`/admin/budget-scenarios/${editing.id}`, payload);
      else await api.post("/admin/budget-scenarios", payload);
      toast.success("Skenario disimpan"); setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Hapus skenario "${s.nama}"?`)) return;
    try { await api.delete(`/admin/budget-scenarios/${s.id}`); toast.success("Dihapus"); await load(); }
    catch { toast.error("Gagal"); }
  };

  return (
    <div>
      <PageTitle title="Skenario Budget" desc="Kelola bundle skenario budget yang bisa diaplikasikan pasangan ke budget mereka." />
      <div className="mb-4 flex justify-end">
        <Button data-testid="admin-scenario-add" onClick={openNew} className="gap-1.5"><Plus className="h-4 w-4" /> Tambah Skenario</Button>
      </div>
      {items.length === 0 ? <EmptyState text="Belum ada skenario." /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {items.map((s) => (
            <Card key={s.id} data-testid={`admin-scenario-row-${s.id}`} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold">{s.nama}</h3>
                  {s.deskripsi && <p className="mt-1 text-xs text-muted-foreground">{s.deskripsi}</p>}
                </div>
                <Badge variant={s.active ? "default" : "outline"}>{s.active ? "Aktif" : "Nonaktif"}</Badge>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{s.items?.length || 0} kategori · Total <b>Rp {Number(s.total || 0).toLocaleString("id-ID")}</b></p>
              <div className="mt-3 flex gap-1.5">
                <Button onClick={() => openEdit(s)} size="sm" variant="outline" className="flex-1"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button onClick={() => remove(s)} size="sm" variant="outline" className="text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Skenario" : "Tambah Skenario"}</DialogTitle><DialogDescription className="sr-only">Form</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Nama Bundle *</Label><Input value={form.nama} onChange={(e) => setForm((f) => ({ ...f, nama: e.target.value }))} data-testid="admin-scenario-nama" placeholder="mis. Intimate Wedding 100 Tamu" /></div>
            <div><Label className="text-xs">Deskripsi</Label><Textarea rows={2} value={form.deskripsi} onChange={(e) => setForm((f) => ({ ...f, deskripsi: e.target.value }))} /></div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label className="text-xs">Rincian per Kategori</Label>
                <Button size="sm" variant="outline" onClick={addItem}><Plus className="h-3 w-3" /> Item</Button>
              </div>
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-[2fr_1.5fr_auto] gap-2">
                    <Input placeholder="Kategori" value={it.kategori} onChange={(e) => setItem(i, "kategori", e.target.value)} />
                    <Input type="number" placeholder="Nominal" value={it.nominal} onChange={(e) => setItem(i, "nominal", e.target.value)} />
                    <Button size="icon" variant="ghost" onClick={() => removeItem(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-right text-sm font-semibold">Total: Rp {total.toLocaleString("id-ID")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
              <Label>{form.active ? "Aktif" : "Nonaktif"}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} data-testid="admin-scenario-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ App Settings (WhatsApp Admin) ============
export function AppSettings() {
  const [wa, setWa] = useState({ number: "", label: "Admin Wedifylab" });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/public/whatsapp-admin").then((r) => setWa({ number: r.data.number || "", label: r.data.label || "Admin Wedifylab" })).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/whatsapp-admin", wa); toast.success("Nomor admin disimpan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <PageTitle title="Pengaturan Aplikasi" desc="Nomor WhatsApp Admin yang tampil di FAB seluruh aplikasi." icon={Phone} />
      <Card className="max-w-lg p-5">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nomor WhatsApp Admin</Label>
        <Input data-testid="wa-admin-number" className="mt-1.5" value={wa.number} onChange={(e) => setWa((s) => ({ ...s, number: e.target.value }))} placeholder="mis. 08123456789 atau 62812345678" />
        <Label className="mt-4 text-xs uppercase tracking-wider text-muted-foreground">Label</Label>
        <Input data-testid="wa-admin-label" className="mt-1.5" value={wa.label} onChange={(e) => setWa((s) => ({ ...s, label: e.target.value }))} placeholder="Admin Wedifylab" />
        <Button data-testid="wa-admin-save" disabled={saving} onClick={save} className="mt-4 gap-1.5"><Save className="h-4 w-4" /> {saving ? "Menyimpan..." : "Simpan"}</Button>
        <p className="mt-3 text-xs text-muted-foreground">
          Jika kosong, ikon WhatsApp di aplikasi akan menampilkan pesan "Belum ada nomor tersedia" saat diklik.
        </p>
      </Card>
    </div>
  );
}
